import { cn } from '@/lib/utils';
import { ConversationList, ChatInterface } from '@/components/molecules';
import type { Conversation } from '@/components/molecules/conversation-list';
import type { Message } from '@/types';
import type { ChatOutboundPayload } from '@/types';
import type { ChannelType, OmnichannelAccount } from '@/types';

export interface ConversationLayoutProps {
  conversations: Conversation[];
  selectedConversation?: string;
  searchQuery: string;
  activeTab: string;
  messages: Message[];
  onConversationSelect: (id: string) => void;
  onSearchChange: (query: string) => void;
  onTabChange: (tab: string) => void;
  onConnectChannel: () => void;
  onDisconnectChannel?: (channel: ChannelType) => void;
  isDisconnectingChannel?: boolean;
  channelConnected?: {
    facebook: boolean;
    instagram: boolean;
    whatsapp: boolean;
  };
  onSendMessage?: (payload: ChatOutboundPayload) => void;
  onRetryMessage?: (messageId: string) => void;
  onReactToMessage?: (messageId: string, emoji: string) => void;
  composerDisabled?: boolean;
  isChannelConnected?: boolean;
  currentChannel?: ChannelType;
  onConnectChannelDirect?: (channel: ChannelType) => void;
  /** Called when user taps back on mobile; clear selection so list shows again */
  onBackFromChat?: () => void;
  conversationCounts?: {
    all: number;
    facebook: number;
    instagram: number;
    whatsapp: number;
  };
  accounts?: OmnichannelAccount[];
  selectedPageId?: string;
  onPageSelect?: (pageId: string) => void;
  isLoadingAccounts?: boolean;
  composerPrefill?: { text: string; key: number } | null;
  onRespondWithAi?: () => void;
  isRespondWithAiLoading?: boolean;
  isAiDraftPending?: boolean;
  canRespondWithAi?: boolean;
  showIncomingMessageNotice?: boolean;
  isRefreshingIncomingMessages?: boolean;
  onRefreshIncomingMessages?: () => void;
  onDismissIncomingMessageNotice?: () => void;
  showTemplates?: boolean;
  onSendTemplate?: (payload: {
    templateId: number;
    templateTitle: string;
    previewBody: string;
    templateVariables: Record<string, string>;
    variableNames: string[];
  }) => void;
  isSendingTemplate?: boolean;
  className?: string;
}

function ConversationLayout({
  conversations,
  selectedConversation,
  searchQuery,
  activeTab,
  messages,
  onConversationSelect,
  onSearchChange,
  onTabChange,
  onConnectChannel,
  onDisconnectChannel,
  isDisconnectingChannel = false,
  channelConnected,
  onSendMessage,
  onRetryMessage,
  onReactToMessage,
  composerDisabled = false,
  isChannelConnected = true,
  currentChannel,
  onConnectChannelDirect,
  onBackFromChat,
  conversationCounts,
  accounts,
  selectedPageId,
  onPageSelect,
  isLoadingAccounts,
  composerPrefill,
  onRespondWithAi,
  isRespondWithAiLoading,
  isAiDraftPending,
  canRespondWithAi,
  showIncomingMessageNotice,
  isRefreshingIncomingMessages,
  onRefreshIncomingMessages,
  onDismissIncomingMessageNotice,
  showTemplates,
  onSendTemplate,
  isSendingTemplate,
  className,
  ...props
}: ConversationLayoutProps) {
  const selectedConv = conversations.find((c) => c.id === selectedConversation);
  const hasSelection = !!selectedConversation;

  return (
    <div
      className={cn('flex lg:space-x-4 h-[90vh] bg-gray-50', className)}
      {...props}
    >
      {/* List: on mobile hide when a conversation is selected; on lg always show */}
      <div
        className={cn(
          'w-full lg:max-w-lg flex flex-col',
          hasSelection ? 'max-lg:hidden' : 'max-lg:flex',
          'lg:flex'
        )}
      >
        <ConversationList
          conversations={conversations}
          selectedConversation={selectedConversation}
          searchQuery={searchQuery}
          activeTab={activeTab}
          onConversationSelect={onConversationSelect}
          onSearchChange={onSearchChange}
          onTabChange={onTabChange}
          onConnectChannel={onConnectChannel}
          onDisconnectChannel={onDisconnectChannel}
          channelConnected={channelConnected}
          isDisconnecting={isDisconnectingChannel}
          conversationCounts={conversationCounts}
          accounts={accounts}
          selectedPageId={selectedPageId}
          onPageSelect={onPageSelect}
          isLoadingAccounts={isLoadingAccounts}
        />
      </div>
      {/* Chat: on mobile show only when a conversation is selected; on lg always show */}
      <div
        className={cn(
          'flex-1 flex flex-col min-w-0',
          hasSelection ? 'max-lg:flex' : 'max-lg:hidden',
          'lg:flex'
        )}
      >
        <ChatInterface
          selectedConversation={selectedConv}
          messages={messages}
          onSendMessage={onSendMessage}
          onRetryMessage={onRetryMessage}
          onReactToMessage={onReactToMessage}
          composerDisabled={composerDisabled}
          isChannelConnected={isChannelConnected}
          currentChannel={currentChannel}
          onConnectChannel={onConnectChannelDirect}
          onBack={onBackFromChat}
          composerPrefill={composerPrefill}
          onRespondWithAi={onRespondWithAi}
          isRespondWithAiLoading={isRespondWithAiLoading}
          isAiDraftPending={isAiDraftPending}
          canRespondWithAi={canRespondWithAi}
          showIncomingMessageNotice={showIncomingMessageNotice}
          isRefreshingIncomingMessages={isRefreshingIncomingMessages}
          onRefreshIncomingMessages={onRefreshIncomingMessages}
          onDismissIncomingMessageNotice={onDismissIncomingMessageNotice}
          showTemplates={showTemplates}
          onSendTemplate={onSendTemplate}
          isSendingTemplate={isSendingTemplate}
        />
      </div>
    </div>
  );
}

export { ConversationLayout };
